import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Download, X, Search, Users, FileText } from 'lucide-react';
import { posApi } from '../services/api';
import { POSCustomer } from '../types';
import ReportDateRangeFilter from './reports/ReportDateRangeFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { formatMoney } from '@/utils/currency';

interface LedgerModalProps {
  isOpen: boolean;
  onClose: () => void;
  customers: POSCustomer[];
  initialCustomer?: string;
}

interface LedgerEntry {
  posting_date: string;
  invoice_no: string;
  payment_no: string;
  description: string;
  items_string?: string;
  debit: number;
  credit: number;
  balance: number;
}

interface LedgerResponse {
  party_name: string;
  opening_balance: number;
  closing_balance: number;
  balance_label: string;
  entries: LedgerEntry[];
  from_date: string;
  to_date: string;
}

const formatDate = (d?: string) => {
  if (!d) return '';
  try {
    return format(new Date(d), 'd-MMM-yyyy');
  } catch {
    return d;
  }
};

export const CustomerLedgerModal: React.FC<LedgerModalProps> = ({ isOpen, onClose, customers: customersProp, initialCustomer }) => {
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [selectedCustomer, setSelectedCustomer] = useState(initialCustomer || '');
  const [selectedCustomerLabel, setSelectedCustomerLabel] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialCustomer && customersProp) {
      setSelectedCustomer(initialCustomer);
      const cust = customersProp.find((c) => c.name === initialCustomer);
      if (cust) {
        setSelectedCustomerLabel(cust.mobile ? `${cust.customer_name} - ${cust.mobile}` : cust.customer_name);
      }
    } else {
      setSelectedCustomer('');
      setSelectedCustomerLabel('');
    }
  }, [initialCustomer, customersProp, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectCustomer = (customer?: POSCustomer) => {
    if (!customer) {
      setSelectedCustomer('');
      setSelectedCustomerLabel('');
    } else {
      setSelectedCustomer(customer.name);
      setSelectedCustomerLabel(
        customer.mobile ? `${customer.customer_name} - ${customer.mobile}` : customer.customer_name
      );
    }
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  const filteredCustomers = customersProp.filter(c => 
    c.customer_name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.mobile?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['partyLedger', 'POS Customer', selectedCustomer, from_date, to_date],
    queryFn: async () => {
      const res = await posApi.getPartyLedger({
        party_type: 'POS Customer',
        party: selectedCustomer || '',
        from_date,
        to_date,
      });
      return (res.data.message || {}) as LedgerResponse;
    },
    enabled: isOpen,
    staleTime: 0,
  });

  const downloadCSV = () => {
    if (!data) return;
    const lines = [
      'Date,Invoice #,Payment #,Description,Debit,Credit,Balance',
      `,,,Opening Balance,,,${data.opening_balance ?? 0}`,
      ...(data.entries || []).map((row) =>
        [
          formatDate(row.posting_date),
          row.invoice_no || '',
          row.payment_no || '',
          `"${(row.description || '').replace(/"/g, '""')}"`,
          row.debit || 0,
          row.credit || 0,
          row.balance ?? 0,
        ].join(',')
      ),
      `,,,Closing Balance,,,${data.closing_balance ?? 0}`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute(
      'download',
      `customer_ledger_${selectedCustomer}_${from_date}_${to_date}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  const closing = data?.closing_balance ?? 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-6xl rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Customer Ledger Report</h2>
            <p className="text-sm text-gray-500 mt-1">View and export detailed customer transaction history</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 border-b border-gray-100 space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            <ReportDateRangeFilter
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              customFrom={customFrom}
              customTo={customTo}
              onCustomFromChange={setCustomFrom}
              onCustomToChange={setCustomTo}
            />

            <div className="space-y-1.5 flex-1 min-w-[200px] sm:min-w-[240px] relative" ref={customerDropdownRef}>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                Customer
              </label>
              {selectedCustomer ? (
                <div className="input-field flex items-center justify-between gap-2 py-2 bg-gray-50 border border-gray-200 shadow-sm rounded-lg">
                  <span className="truncate text-sm text-gray-900 font-medium px-1">{selectedCustomerLabel}</span>
                  <button
                    type="button"
                    onClick={() => handleSelectCustomer()}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                    aria-label="Clear customer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      className="input-field pl-9"
                      placeholder="Search by name or phone..."
                      value={customerSearch}
                      onChange={(e) => {
                        setCustomerSearch(e.target.value);
                        setShowCustomerDropdown(true);
                      }}
                      onFocus={() => setShowCustomerDropdown(true)}
                    />
                  </div>
                  {showCustomerDropdown && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                      {filteredCustomers.length > 0 ? (
                        filteredCustomers.map((c) => (
                          <button
                            key={c.name}
                            type="button"
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => handleSelectCustomer(c)}
                          >
                            <div className="font-medium">{c.customer_name}</div>
                            {c.mobile && <div className="text-xs text-gray-500">{c.mobile}</div>}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">No customers found</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex gap-2 w-full sm:w-auto mt-4 sm:mt-0 ml-auto">
              <button
                onClick={downloadCSV}
                disabled={isLoading || !data?.entries?.length}
                className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gray-50/30">
          {isLoading || isFetching ? (
            <div className="flex items-center justify-center h-full p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : data ? (
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-500 bg-white uppercase sticky top-0 shadow-sm z-10 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 font-medium whitespace-nowrap">Date</th>
                  <th className="px-6 py-4 font-medium">Invoice No</th>
                  <th className="px-6 py-4 font-medium">Payment No</th>
                  <th className="px-6 py-4 font-medium">Description</th>
                  <th className="px-6 py-4 font-medium text-right text-red-600">Debit</th>
                  <th className="px-6 py-4 font-medium text-right text-emerald-600">Credit</th>
                  <th className="px-6 py-4 font-medium text-right">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                <tr className="bg-gray-50/50">
                  <td colSpan={6} className="px-6 py-3 font-medium text-gray-600 text-right">Opening Balance</td>
                  <td className="px-6 py-3 text-right font-semibold text-gray-900">
                    {formatMoney(data.opening_balance ?? 0)}
                  </td>
                </tr>
                {(data.entries || []).map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-6 py-3 whitespace-nowrap text-gray-500">
                      {formatDate(row.posting_date)}
                    </td>
                    <td className="px-6 py-3 text-gray-900 font-medium">
                      {row.invoice_no || '-'}
                    </td>
                    <td className="px-6 py-3 text-gray-900 font-medium">
                      {row.payment_no || '-'}
                    </td>
                    <td className="px-6 py-3 text-gray-600 max-w-xs xl:max-w-md">
                      <div className="font-medium text-gray-900">{row.description}</div>
                    </td>
                    <td className="px-6 py-3 text-right text-red-600">
                      {row.debit > 0 ? formatMoney(row.debit) : '-'}
                    </td>
                    <td className="px-6 py-3 text-right text-emerald-600">
                      {row.credit > 0 ? formatMoney(row.credit) : '-'}
                    </td>
                    <td className="px-6 py-3 text-right font-medium text-gray-900">
                      {formatMoney(row.balance)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 border-t-2 border-gray-200">
                  <td colSpan={4} className="px-6 py-4">
                    <div className="flex justify-end items-center gap-2">
                      <span className="font-medium text-gray-600">Closing Balance:</span>
                      <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded-md font-medium">
                        {data.balance_label}
                      </span>
                    </div>
                  </td>
                  <td colSpan={3} className="px-6 py-4 text-right">
                    <span className={`text-lg font-bold ${closing > 0 ? 'text-red-600' : closing < 0 ? 'text-emerald-600' : 'text-gray-900'}`}>
                      {formatMoney(Math.abs(closing))}
                    </span>
                    {closing !== 0 && (
                      <span className="text-xs font-medium text-gray-500 ml-1">
                        {closing > 0 ? 'DR' : 'CR'}
                      </span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center text-gray-500">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Transactions Found</h3>
              <p>There are no ledger entries for the selected period.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerLedgerModal;
